/*
 * Copyright (c) 2024 metromorph softworks GmbH
 *
 * All rights reserved
 */

import { AbstractData, NewData, DataController } from "@ef/common-api";

import { AxiosInstance, HttpStatusCode } from "axios";

import { 
    User,
    getApiForUser
} from "@ef/backend-core";

export type ApiVersion = "v2" | "v3";

/**
 * Connect to Simple CRUD API
 */
export class RestApiDataController<T extends AbstractData> implements DataController<T> {

    api: AxiosInstance;
    version: ApiVersion;
    prefix: string;

    constructor(user: User, prefix: string, version: ApiVersion) {
        this.api = getApiForUser(user);
        if (version == "v2") {
            this.prefix = prefix;
        } else {
            this.prefix = `/data${prefix}`;
        }
        this.version = version;
    }

    protected getUrl(url: string): string {
        return `/${this.version}${this.prefix}${url}`;
    }

    async get(id: string): Promise<T|undefined> {
        const res = await this.api.get(this.getUrl(`/${id}`), {
            validateStatus: (status) => (
                status == HttpStatusCode.Ok || status == HttpStatusCode.NotFound
            )
        });
        switch (res.status) {
            case HttpStatusCode.Ok:
                return res.data;
            case HttpStatusCode.NotFound:
            default:
                return undefined;
        }
    }

    async find(/*filter?: Filter, opts?: FilterOptions*/): Promise<Iterable<T>> {
        const res = await this.api.get(this.getUrl("/"), {
            validateStatus: (status) => (
                status == HttpStatusCode.Ok
            )
        });
        if (this.version == "v2") {
            return res.data;
        } else {
            return res.data.data ?? [];
        }
    }

    async create(data: NewData<T>): Promise<T> {
        const res =  await this.api.post(this.getUrl("/"), data, {
            validateStatus: (status) => (
                status == HttpStatusCode.Created 
            )
        });
        return res.data;
    }

    async save(data: T): Promise<T> {
        const res = await this.api.put(this.getUrl(`/${data._id}`), data, {
            validateStatus: (status) => (
                status == HttpStatusCode.Ok
            )
        });
        return res.data;
    }
}

/* c8 ignore end */
